import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { api } from '../api.ts';
import { dateLabel } from '../format.ts';
import { Icon } from './Icons.tsx';

// Journaling progress: closed trades from the last 30 days with notes, a setup and a rating. Shown in the
// sidebar under the main pages (desktop), and as a badge on the Today tab (phones).

export type JournalStatusData = { windowDays: number; total: number; done: number; next: { id: number; symbol: string; tradeDate: string } | null };

const CHANGED = 'tj:journal-changed';
/** Call after saving a trade's journal so every status display refreshes. */
export const journalChanged = () => window.dispatchEvent(new Event(CHANGED));

export function useJournalStatus() {
  const [status, setStatus] = useState<JournalStatusData | null>(null);
  const { pathname } = useLocation();
  useEffect(() => {
    let cancelled = false;
    const load = () => api.get<JournalStatusData>('/api/journal-status').then((s) => { if (!cancelled) setStatus(s); }).catch(() => {});
    void load();
    window.addEventListener(CHANGED, load);
    return () => { cancelled = true; window.removeEventListener(CHANGED, load); };
  }, [pathname]); // also refresh on navigation (e.g. after an import)
  return status;
}

/**
 * Progress with a link to the next trade to journal. `variant` picks the look: a slim line in the
 * expanded sidebar, a ring in the collapsed one, or a bar on the dashboard (phones).
 */
export function JournalStatus({ status, variant }: { status: JournalStatusData | null; variant: 'sidebar' | 'rail' | 'banner' }) {
  if (!status || status.total === 0) return null;
  const left = status.total - status.done;
  const pct = Math.round((status.done / status.total) * 100);
  const bar = <div className="journal-bar" aria-hidden="true"><div style={{ width: `${pct}%` }} /></div>;
  const label = `${status.done} / ${status.total} trades journaled in the last ${status.windowDays} days (notes, setup and rating)`;

  if (variant === 'rail') {
    const r = 11, c = 2 * Math.PI * r;
    const ring = (
      <svg viewBox="0 0 28 28" width="28" height="28" aria-hidden="true">
        <circle cx="14" cy="14" r={r} className="ring-track" />
        <circle cx="14" cy="14" r={r} className="ring-fill" strokeDasharray={c} strokeDashoffset={c * (1 - status.done / status.total)} transform="rotate(-90 14 14)" />
      </svg>
    );
    return status.next
      ? <Link to={`/trades/${status.next.id}`} className="journal-ring" title={`${label}. Next: ${status.next.symbol}`} aria-label={`${left} trades to journal. Open the next one.`}>{ring}</Link>
      : <div className="journal-ring done" title="Journal up to date" aria-label="Journal up to date">{ring}</div>;
  }

  if (!status.next) {
    return variant === 'sidebar'
      ? <div className="journal-status in-sidebar done" title={label}><span>Journal up to date</span><span className="gi done-ico"><Icon.check /></span></div>
      : null; // nothing to nag about on the dashboard
  }
  const next = status.next;
  return (
    <Link to={`/trades/${next.id}`} className={`journal-status in-${variant}`} title={label}>
      <div className="journal-status-row">
        <span>{variant === 'banner' ? `${left} trade${left === 1 ? '' : 's'} to journal` : 'Journaling'}</span>
        <b>{status.done} / {status.total}</b>
      </div>
      {bar}
      <span className="journal-next">Next: {next.symbol}, {dateLabel(next.tradeDate, { month: 'short', day: 'numeric' })}</span>
    </Link>
  );
}
